import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { BASE_URL } from '@/config/api';
import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  User,
  Users,
  FileText,
  X,
  Trash2,
  Info,
  GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

interface EventDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: any;
  students: { id: string; firstname: string; lastname: string; name?: string }[];
  events: any;
  onRefreshEvents?: () => void;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  open,
  onOpenChange,
  event,
  students,
  events,
  onRefreshEvents
}) => {
  const [isOwned, setIsOwned] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { user, token } = useAuth();
  const { toast } = useToast();
  const { t, locale } = useLanguage();
  const isMobile = useIsMobile();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const formatTime = (date: Date, locale: string) => {
    if (locale === 'fr') {
      return format(new Date(date), "HH'h'mm");
    } else {
      return format(new Date(date), 'h:mm a');
    }
  };

  const formatEventDateRange = (startStr: string, endStr: string, locale: string): string => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (!endStr || end <= start) {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(start);
    }
    const isSameDay = start.getDate() === end.getDate() && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const isSameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const isSameYear = start.getFullYear() === end.getFullYear();
    if (isSameDay) {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(start);
    }
    if (isSameMonth) {
      const startDay = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric' }).format(start);
      const endDay = new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(end);
      const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(start);
      const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(start);
      return `Du ${startDay} au ${endDay} ${month} ${year}`;
    }
    if (isSameYear) {
      const startDayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(start);
      const endDayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(end);
      const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(start);
      return `Du ${startDayMonth} au ${endDayMonth} ${year}`;
    }
    const fullStart = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(start);
    const fullEnd = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(end);
    return `Du ${fullStart} au ${fullEnd}`;
  };

  const getEventTypeDescription = (eventType: number) => {
    switch (eventType) {
      case 10: return 'Lesson';
      case 50: return 'Coach Off';
      case 51: return 'Student Off';
      case 100: return 'Event';
      default: return 'Type inconnu';
    }
  };

  useEffect(() => {
    if (event && open) {
      const owned = !!event.extendedProps?.isEventOwned;
      setIsOwned(owned);
    }
  }, [event, open]);

  const confirmDeleteEvent = async () => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${BASE_URL}/api/delete_event`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: event.id, deleteAllRecurrences: false })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('agenda.failedToDeleteEvent'));
      }
      toast({
        title: t('agenda.eventDeleted'),
        description: t('agenda.eventDeletedSuccessfully'),
      });
      onOpenChange(false);
      if (onRefreshEvents) {
        onRefreshEvents();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: t('agenda.errorDeletingEvent'),
        description: error instanceof Error ? error.message : t('agenda.pleaseTryAgain'),
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  if (!event) return null;

  const startTime = formatTime(event.start, locale);
  const endTime = formatTime(event.end, locale);
  const eventDate = format(new Date(event.start), 'EEEE, MMMM d, yyyy');
  const eventDateEnd = format(new Date(event.end), 'EEEE, MMMM d, yyyy');
  const isStartIsSameEnd = eventDate === eventDateEnd;
  const isEventPast = new Date(event.start) < new Date();
  const eventStudents = event.extendedProps?.students || [];
  const location = event.extendedProps?.locationId ? { name: event.extendedProps.locationName } : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-blue-500" />
              {getEventTypeDescription(event.extendedProps.type)}
            </div>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className={`${isMobile ? "max-h-[calc(100dvh-180px)]" : "max-h-[60vh]"} overflow-auto`}>
          <div className={`${isMobile ? "px-4" : ""} py-4 space-y-6`}>
            {user.account_type === 'Student' && event.extendedProps.teacherName && (
                <div className="flex items-center space-x-2 font-semibold">
                  <GraduationCap className="h-5 w-5 text-muted-foreground" />
                  <span>{event.extendedProps.teacherName}</span>
                </div>
              )}
            {event.title && (
              <div className="flex items-center space-x-2">
                <span>{event.title}</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-muted-foreground" />
              <span>{formatEventDateRange(event.start, event.end, locale)}</span>
              <div className="flex flex-wrap gap-1 ml-7">
                {isEventPast && (
                  <Badge key="past-incoming" variant="secondary">
                    <span>{isEventPast ? t('agenda.past') : t('agenda.upcoming')}</span>
                  </Badge>
                )}
              </div>
            </div>
            {!event.allDay && (
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span>{startTime} - {endTime}</span>
              </div>
            )}
            {location && (
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <span>{location.name}</span>
              </div>
            )}
            {eventStudents.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <span><strong>{t('students.students')}:</strong></span>
                  </div>
                  <div className="flex flex-wrap gap-1 ml-7">
                    {eventStudents.map((student) => (
                      <Badge key={student.id} className="text-xs cursor-pointer flex items-center" variant="secondary">
                        <span>
                          {student.name || `${student.firstname} ${student.lastname}`}
                        </span>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {event.extendedProps?.note && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <span><strong>{t('agenda.note')}:</strong></span>
                </div>
                <div className="ml-7 text-sm border-l-2 pl-3 py-1 border-muted">
                  {event.extendedProps.note}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
        {!showDeleteConfirmation && event.extendedProps.type === 51 && isOwned && (
          <div className="p-4 border-t flex justify-end">
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={isDeleting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('common.delete')}
            </Button>
          </div>
        )}
        {showDeleteConfirmation && (
          <div className="p-4 border-t bg-red-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{t('agenda.confirmDeletion')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('agenda.confirmDelete')}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="destructive" onClick={confirmDeleteEvent} disabled={isDeleting}>
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailModal;
